import { useEffect, useMemo, useState } from 'react';
import { logger } from 'features/logger/logger';
import { selectOptionalParticipationDocData } from 'features/participations/store/participationSelectors';
import { useGetProseDslSourceByAssetIdQuery } from 'features/questionnaires/store/questionnaireAssetApiSlice';
import { useAppSelector } from 'features/store/hooks';
import { getTimezoneName } from 'shared/utils/dateUtils';
import { logUnlessPageLeft } from 'shared/utils/pageLeave';
import { localDayBucket } from '../logic/localDay';
import { computeNextBoundary } from '../logic/nextBoundary';
import { projectTasks } from '../logic/scheduleViewModel';
import { computeParticipantSchedule } from '../services/computeParticipantSchedule';
import { preloadParticipationAssets } from '../services/preloadParticipationAssets';
import type { ProjectedTask } from '../types/ScheduleTask';
import type { Activity } from '@castoredc/castor_prose';

// Computes the active participation's projected tasks from PROSE (manifest
// definition + decoded participantState) and re-derives availability at each
// task-window boundary via a single `setTimeout` — no polling. Holds no
// Firestore subscription: availability is a pure function of the PROSE-computed
// task window and the current time, so the task page can resolve it without the
// tasks collection. `useParticipationSchedule` layers the task-document join on top.
// `isRefreshing` is true while `tasks` still come from an earlier
// `participantState` than the participation document carries.
export const useProjectedTasks = (): {
  tasks: ProjectedTask[];
  isLoading: boolean;
  isRefreshing: boolean;
} => {
  const participation = useAppSelector(selectOptionalParticipationDocData);
  const participationId = participation?.id;
  const manifest = participation?.manifest;
  const definition = manifest?.definition;
  const participantState = participation?.participantState;
  const timezone = getTimezoneName();

  // Warm the manifest's style/images into cache as soon as the participation is
  // available, so opening a task doesn't flash default styling. Deduped
  // internally; the DSL is warmed by the query below.
  useEffect(() => {
    if (manifest) {
      preloadParticipationAssets(manifest);
    }
  }, [manifest]);

  const {
    data: dslSource,
    isError: isDslError,
    error: dslError,
  } = useGetProseDslSourceByAssetIdQuery(definition ?? '', {
    skip: !definition,
  });

  const [computed, setComputed] = useState<{
    activities: Activity[];
    participantState: string | undefined;
  } | null>(null);
  const [now, setNow] = useState(() => new Date());

  // The compute memo folds in the local-day bucket as a daily freshness
  // backstop. Recompute when the local day rolls over: the boundary timer
  // advances `now` past local midnight (see computeNextBoundary), which changes
  // this bucket and re-runs the compute effect. Ordinary availability
  // boundaries move `now` within the same day, so the bucket is unchanged and
  // PROSE is not re-run.
  const dayBucket = localDayBucket(now);

  useEffect(() => {
    if (isDslError) {
      logUnlessPageLeft(
        'useProjectedTasks: failed to load schedule DSL source',
        dslError
      );
      setComputed({ activities: [], participantState });
      return;
    }
    // A participation without a schedule definition (legacy participations
    // carry an empty one) has no schedule to compute; resolve to an empty
    // schedule so home renders its empty state instead of a spinner.
    if (!definition) {
      setComputed({ activities: [], participantState });
      return;
    }
    if (!dslSource || participantState === undefined || !participationId)
      return;
    let cancelled = false;
    void computeParticipantSchedule({
      dslSource,
      participationId,
      definitionKey: definition,
      participantState,
      timezone,
      now: new Date(),
    })
      .then(result => {
        if (!cancelled) {
          setComputed({ activities: result, participantState });
          setNow(new Date());
        }
      })
      .catch((error: unknown) => {
        logger.error(
          'useProjectedTasks: computeParticipantSchedule failed',
          undefined,
          error instanceof Error ? error : new Error(String(error))
        );
        // Resolve the loading state to an empty schedule so home shows its empty
        // state instead of an indefinite spinner.
        if (!cancelled) {
          setComputed({ activities: [], participantState });
          setNow(new Date());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    dslSource,
    participationId,
    definition,
    participantState,
    timezone,
    isDslError,
    dslError,
    dayBucket,
  ]);

  const tasks = useMemo(
    () => (computed ? projectTasks(computed.activities, now) : []),
    [computed, now]
  );

  useEffect(() => {
    const boundary = computeNextBoundary(tasks, now);
    if (!boundary) return;
    const id = setTimeout(
      () => setNow(new Date()),
      Math.max(0, boundary.getTime() - Date.now())
    );
    return () => clearTimeout(id);
  }, [tasks, now]);

  const isLoading = computed === null;
  const isRefreshing =
    computed !== null && computed.participantState !== participantState;
  return { tasks, isLoading, isRefreshing };
};
